import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Checkbox, notification, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import { useCompanyOptions } from '../../hooks/useCompanyOptions';
import { validateRedirectUrl } from '../../utils/redirectValidator';
import { VENDOR_LOGIN_ROUTE } from '../../constants/routes';
import AuthShell from '../AuthShell/AuthShell';
import { userKeyRules, isKeyNotOnRecord, NoEmailOnRecord } from '../AuthShell/userKey';
import {
  UserIcon, LockIcon, ChevronIcon, EyeIcon, EyeOffIcon, MicrosoftIcon,
} from '../AuthShell/icons';

const AZURE_API_URL = (process.env.REACT_APP_AZURE_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '');
const REMEMBERED_USER_KEY = 'remembered_username';

const RequiredLabel = ({ text }: { text: string }) => (
  <span>{text} <span className="login-required">*</span></span>
);

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { companyData, companyLocked, resolvingCompany, unknownKey, setInputCode } = useCompanyOptions(form);
  const [username, setUsername] = useState('');

  const [loading, setLoading] = useState(false);
  const [azureLoading, setAzureLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Redirect when already authenticated is handled by PublicRoute (with handoff token)

  /**
   * Show Azure SSO error if redirected back with ?error=...
   */
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      setErrorMsg(decodeURIComponent(error));
    }
  }, [searchParams]);

  /**
   * Restore the remembered user name
   */
  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBERED_USER_KEY);
    if (remembered) {
      form.setFieldsValue({ username: remembered, remember: true });
      setUsername(remembered);
      setInputCode(remembered);
    }
  }, [form, setInputCode]);

  const typedKey = username.trim();

  /** E Code or email the backend has confirmed matches no active user. */
  const keyNotOnRecord = isKeyNotOnRecord(typedKey, unknownKey);

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    setInputCode(value);
  };

  /**
   * Handle login form submission
   */
  const handleLogin = async (values: any) => {
    // Nothing to authenticate against — the inline notice already tells the
    // user to get the email ID updated, so don't spend a round trip on it.
    if (keyNotOnRecord) return;

    setLoading(true);
    setErrorMsg('');
    try {
      const success = await login({
        username: values.username,
        password: values.password,
        company_id: values.company_id,
      });

      if (success) {
        if (values.remember) {
          localStorage.setItem(REMEMBERED_USER_KEY, values.username);
        } else {
          localStorage.removeItem(REMEMBERED_USER_KEY);
        }

        const rawRedirectUrl = searchParams.get('redirectUrl') || localStorage.getItem('redirect_url');
        const defaultUrl = process.env.REACT_APP_WEBFRONT_URL || 'http://localhost:4800/universe';
        const destination = validateRedirectUrl(rawRedirectUrl, defaultUrl);
        localStorage.removeItem('redirect_url');
        const handoffToken = await authService.generateHandoffToken();

        const separator = destination.includes('?') ? '&' : '?';
        window.location.href = handoffToken
          ? `${destination}${separator}handoffToken=${handoffToken}`
          : destination;
      } else {
        setErrorMsg('Login failed — The username or password you entered is incorrect.');
      }
    } catch (error: any) {
      message.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initiate Azure AD / Microsoft SSO login (server-side PKCE flow).
   * Company is required so the backend can scope the user lookup.
   */
  const handleAzureLogin = async () => {
    let company_id: any;
    try {
      ({ company_id } = await form.validateFields(['company_id']));
    } catch (err) {
      return notification.error({ message: 'Please select a company to continue' });
    }
    setErrorMsg('');
    setAzureLoading(true);
    const rawRedirect = searchParams.get('redirectUrl') || localStorage.getItem('redirect_url') || '';
    const redirectUrl = validateRedirectUrl(rawRedirect, '');
    const params = new URLSearchParams({ company_id: String(company_id) });
    if (redirectUrl) params.set('redirectUrl', redirectUrl);
    window.location.href = `${AZURE_API_URL}/auth/azure/login?${params.toString()}`;
  };

  /** Hand off to the reset flow, keeping ?redirectUrl for the round trip. */
  const goToForgotPassword = () => {
    const search = searchParams.toString();
    navigate(search ? `/forgot-password?${search}` : '/forgot-password');
  };

  /** Vendors sign in by OTP instead — carry ?redirectUrl across with them. */
  const goToVendorLogin = () => {
    const search = searchParams.toString();
    navigate(search ? `${VENDOR_LOGIN_ROUTE}?${search}` : VENDOR_LOGIN_ROUTE);
  };

  const onFailed = (errorInfo: any) => {
    console.log('Form validation failed:', errorInfo);
  };

  return (
    <AuthShell tagline="Welcome back — sign in to continue">
      <Form
        form={form} name="login" layout="vertical" className="login-form"
        initialValues={{ remember: false }}
        onFinish={handleLogin} onFinishFailed={onFailed} autoComplete="off"
      >
        {errorMsg ? <div className="login-alert">{errorMsg}</div> : null}

        <Form.Item
          label={<RequiredLabel text="E Code/ Email" />} name="username"
          rules={userKeyRules}
        >
          <Input
            prefix={<UserIcon />} placeholder="Enter your E Code/Email"
            onChange={(e) => handleUsernameChange(e?.target?.value ?? '')}
          />
        </Form.Item>
        {keyNotOnRecord ? <NoEmailOnRecord /> : null}

        <Form.Item
          label={<RequiredLabel text="Password" />} name="password"
          rules={[{ required: true, message: 'Password is required' }]}
        >
          <Input.Password
            prefix={<LockIcon />} placeholder="Enter your password"
            iconRender={(visible) => (
              <span className="login-eye">{visible ? <EyeIcon /> : <EyeOffIcon />}</span>
            )}
          />
        </Form.Item>

        <Form.Item
          label={<RequiredLabel text="Company" />} name="company_id"
          className="login-select-field"
          rules={[{ required: true, message: 'Company is required' }]}
        >
          <Select
            showSearch placeholder="Select Company"
            optionFilterProp="children"
            suffixIcon={<ChevronIcon />}
            disabled={companyLocked}
            loading={resolvingCompany}
            filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            options={companyData?.map((item: any) => ({ value: item.id, label: item.name }))}
          />
        </Form.Item>
        {companyLocked ? (
          <div className="login-field-hint">Auto-selected from your E Code/Email</div>
        ) : null}

        <div className="login-options">
          <Form.Item name="remember" valuePropName="checked" noStyle>
            <Checkbox>Remember me</Checkbox>
          </Form.Item>
          <button type="button" className="login-link" onClick={goToForgotPassword}>Forgot password?</button>
        </div>

        <Button
          type="primary" htmlType="submit"
          loading={loading} className="login-submit-btn"
        >
          Login
        </Button>

        <div className="login-divider"><span>or sign in with SSO</span></div>

        <Button
          className="login-azure-btn"
          onClick={handleAzureLogin}
          loading={azureLoading}
          icon={azureLoading ? undefined : <MicrosoftIcon />}
        >
          Sign in with Azure AD
        </Button>

        <div className="login-alt-action">
          Are you a vendor?{' '}
          <button type="button" className="login-link" onClick={goToVendorLogin}>
            Sign in with mobile OTP
          </button>
        </div>
      </Form>
    </AuthShell>
  );
};

export default Login;
